import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Target, ArrowLeft } from "lucide-react";
import { api, ZONES } from "@/lib/handball";
import GoalGrid from "@/components/GoalGrid";
import TournamentFilterBar, { GoalkeeperComparisonCard, TournamentMatchList } from "@/components/TournamentFilterBar";

export default function ShareView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tournamentFilter, setTournamentFilter] = useState({ goalkeeperId: null, sessionId: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const params = {};
        if (tournamentFilter.goalkeeperId) params.goalkeeper_id = tournamentFilter.goalkeeperId;
        if (tournamentFilter.sessionId) params.session_id = tournamentFilter.sessionId;
        const res = await api.get(`/share/${token}`, { params });
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled)
          setError(
            e?.response?.status === 404
              ? "Delningen hittades inte"
              : "Kunde inte ladda delning"
          );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, tournamentFilter]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-zinc-400 font-display uppercase tracking-widest">
        Laddar delning…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <div
          data-testid="share-error"
          className="font-display font-black uppercase tracking-widest text-3xl text-white"
        >
          {error || "Fel vid laddning"}
        </div>
        <Link
          to="/"
          className="text-[#34C759] font-display uppercase tracking-widest text-sm hover:underline"
        >
          Till startsidan
        </Link>
      </div>
    );
  }

  const { goalkeeper, session, scope, stats, tournament, matches, goalkeepers: tournGks, active_session } = data;

  // Tournament-scoped share — render tournament name + aggregated stats + match list
  if (scope === "tournament") {
    const activeGk = tournamentFilter.goalkeeperId
      ? (tournGks || []).find((g) => g.id === tournamentFilter.goalkeeperId)
      : null;
    const title = active_session?.name || activeGk?.name || tournament.name;
    const subtitle = active_session
      ? `${activeGk ? `${activeGk.name} · ` : ""}${active_session.opponent ? `vs ${active_session.opponent} · ` : ""}${tournament.name}`
      : activeGk
      ? tournament.name
      : `${tournament.start_date} — ${tournament.end_date}`;
    return (
      <div className="min-h-screen relative">
        <header className="border-b border-[#27272A] bg-[#0A0A0A]/80 backdrop-blur-md sticky top-0 z-30">
          <div className="max-w-[1200px] mx-auto px-6 py-4 flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 border-2 border-[#34C759] flex items-center justify-center">
                <Target size={20} className="text-[#34C759]" strokeWidth={2.5} />
              </div>
              <div>
                <div className="font-display font-black text-xl uppercase tracking-widest leading-none">
                  Målvaktslabb
                </div>
                <div className="text-xs text-zinc-500 uppercase tracking-[0.2em] mt-1">
                  Delad turnering · Läsläge
                </div>
              </div>
            </div>
            <Link
              to="/"
              className="ml-auto flex items-center gap-2 h-11 px-4 border border-[#27272A] hover:bg-[#171717] text-white font-display uppercase tracking-widest text-sm"
              data-testid="share-home-link"
            >
              <ArrowLeft size={16} />
              Öppna app
            </Link>
          </div>
        </header>

        <main className="max-w-[1200px] mx-auto px-6 py-8 space-y-6">
          <section className="card-tactical p-6">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-display mb-2">
              Turnering/serie
            </div>
            <div
              className="font-display font-black uppercase tracking-widest text-3xl md:text-4xl text-white"
              data-testid="share-tournament-name"
            >
              {title}
            </div>
            <div className="mt-2 text-zinc-400 font-display uppercase tracking-widest text-sm">
              {subtitle}
            </div>
          </section>

          <TournamentFilterBar
            goalkeepers={tournGks || []}
            matches={matches || []}
            value={tournamentFilter}
            onChange={setTournamentFilter}
          />

          {!tournamentFilter.goalkeeperId && !tournamentFilter.sessionId && (
            <GoalkeeperComparisonCard goalkeepers={tournGks || []} />
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi label="Skott på mål" value={stats.total} testid="share-kpi-total" />
            <Kpi label="Räddningar" value={stats.saves} testid="share-kpi-saves" />
            <Kpi label="Mål" value={stats.goals} testid="share-kpi-goals" />
            <Kpi
              label="Räddning %"
              value={`${stats.save_pct.toFixed(1)}%`}
              testid="share-kpi-savepct"
              accent
            />
          </div>

          <section className="card-tactical p-6">
            <div className="section-hdr mb-5">
              <span className="marker" />
              Målbild · Räddningsprocent per ruta
            </div>
            <div className="max-w-[600px] mx-auto">
              <GoalGrid
                stats={stats.placement}
                testId="share-goal-grid"
                testIdPrefix="share-cell"
              />
            </div>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <BreakdownCard title="Per zon" data={stats.zones} order={ZONES} />
            <BreakdownCard
              title="Per avstånd"
              data={stats.distances}
              order={["9m", "mellan", "6m", "7m"]}
              labels={{ "9m": "9M", mellan: "MELLAN", "6m": "6M", "7m": "7M STRAFF" }}
            />
          </div>

          {stats.contacts &&
            Object.values(stats.contacts).some((v) => (v?.total || 0) > 0) && (
              <BreakdownCard
                title="Per kontakt"
                data={stats.contacts}
                order={["yes", "no"]}
                labels={{ yes: "JA", no: "NEJ" }}
              />
            )}

          {stats.lobs &&
            Object.values(stats.lobs).some((v) => (v?.total || 0) > 0) && (
              <BreakdownCard
                title="Per lobb"
                data={stats.lobs}
                order={["yes", "no"]}
                labels={{ yes: "JA", no: "NEJ" }}
              />
            )}

          <TournamentMatchList
            matches={matches || []}
            value={tournamentFilter}
            onChange={setTournamentFilter}
            testid="share-tournament-matches"
            itemPrefix="share-tournament-match"
          />
        </main>

        <footer className="max-w-[1200px] mx-auto px-6 py-6 text-[10px] text-zinc-600 uppercase tracking-[0.25em] font-display">
          Delad turnering · Målvaktslabb
        </footer>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative">
      <header className="border-b border-[#27272A] bg-[#0A0A0A]/80 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-[1200px] mx-auto px-6 py-4 flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 border-2 border-[#34C759] flex items-center justify-center">
              <Target size={20} className="text-[#34C759]" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-display font-black text-xl uppercase tracking-widest leading-none">
                Målvaktslabb
              </div>
              <div className="text-xs text-zinc-500 uppercase tracking-[0.2em] mt-1">
                Delad statistik · Läsläge
              </div>
            </div>
          </div>
          <Link
            to="/"
            className="ml-auto flex items-center gap-2 h-11 px-4 border border-[#27272A] hover:bg-[#171717] text-white font-display uppercase tracking-widest text-sm"
            data-testid="share-home-link"
          >
            <ArrowLeft size={16} />
            Öppna app
          </Link>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-6 py-8 space-y-6">
        {/* Header block */}
        <section className="card-tactical p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-display mb-2">
            {scope === "session" ? "Match / Träning" : "All samlad statistik"}
          </div>
          <div
            className="font-display font-black uppercase tracking-widest text-3xl md:text-4xl text-white"
            data-testid="share-goalkeeper-name"
          >
            {goalkeeper.name}
          </div>
          {session ? (
            <div
              className="mt-2 text-zinc-400 font-display uppercase tracking-widest text-sm"
              data-testid="share-session-info"
            >
              {session.name} ·{" "}
              {session.session_type === "match" ? "Match" : "Träning"}
              {session.opponent ? ` · vs ${session.opponent}` : ""}
            </div>
          ) : (
            <div
              className="mt-2 text-zinc-400 font-display uppercase tracking-widest text-sm"
              data-testid="share-session-info"
            >
              Alla matcher och träningar
            </div>
          )}
        </section>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label="Skott på mål" value={stats.total} testid="share-kpi-total" />
          <Kpi label="Räddningar" value={stats.saves} testid="share-kpi-saves" />
          <Kpi label="Mål" value={stats.goals} testid="share-kpi-goals" />
          <Kpi
            label="Räddning %"
            value={`${stats.save_pct.toFixed(1)}%`}
            testid="share-kpi-savepct"
            accent
          />
        </div>

        {/* Goal grid */}
        <section className="card-tactical p-6">
          <div className="section-hdr mb-5">
            <span className="marker" />
            Målbild · Räddningsprocent per ruta
          </div>
          <div className="max-w-[600px] mx-auto">
            <GoalGrid
              stats={stats.placement}
              testId="share-goal-grid"
              testIdPrefix="share-cell"
            />
          </div>
        </section>

        {/* Breakdown */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <BreakdownCard title="Per zon" data={stats.zones} order={ZONES} />
          <BreakdownCard
            title="Per avstånd"
            data={stats.distances}
            order={["9m", "mellan", "6m", "7m"]}
            labels={{ "9m": "9M", mellan: "MELLAN", "6m": "6M", "7m": "7M STRAFF" }}
          />
        </div>

        {stats.contacts &&
          Object.values(stats.contacts).some((v) => (v?.total || 0) > 0) && (
            <BreakdownCard
              title="Per kontakt"
              data={stats.contacts}
              order={["yes", "no"]}
              labels={{ yes: "JA", no: "NEJ" }}
            />
          )}

        {stats.lobs &&
          Object.values(stats.lobs).some((v) => (v?.total || 0) > 0) && (
            <BreakdownCard
              title="Per lobb"
              data={stats.lobs}
              order={["yes", "no"]}
              labels={{ yes: "JA", no: "NEJ" }}
            />
          )}
      </main>

      <footer className="max-w-[1200px] mx-auto px-6 py-6 text-[10px] text-zinc-600 uppercase tracking-[0.25em] font-display">
        Delad statistik · Målvaktslabb
      </footer>
    </div>
  );
}

function Kpi({ label, value, accent, testid }) {
  return (
    <div className="kpi" data-testid={testid}>
      <div className="kpi-label">{label}</div>
      <div
        className="kpi-value font-mono-num"
        style={{ color: accent ? "#34C759" : "#ffffff" }}
      >
        {value}
      </div>
    </div>
  );
}

function BreakdownCard({ title, data, order, labels = {} }) {
  return (
    <div className="card-tactical p-5">
      <div className="section-hdr mb-4">
        <span className="marker" />
        {title}
      </div>
      <div className="space-y-2">
        {order.map((key) => {
          const d = data?.[key] ?? { total: 0, saves: 0, save_pct: 0 };
          const pct = d.total ? d.save_pct : 0;
          return (
            <div
              key={key}
              className="grid grid-cols-[70px_1fr_90px] items-center gap-3"
            >
              <span className="font-display font-bold uppercase tracking-widest text-sm">
                {labels[key] ?? key}
              </span>
              <div className="h-2 bg-[#171717] border border-[#27272A] relative">
                <div
                  className="h-full"
                  style={{
                    width: `${pct}%`,
                    background:
                      pct >= 66 ? "#34C759" : pct >= 33 ? "#FFD60A" : "#FF3B30",
                    transition: "width 0.3s ease",
                  }}
                />
              </div>
              <span className="text-xs text-zinc-400 font-mono-num text-right">
                {d.saves}/{d.total} · {d.total ? d.save_pct.toFixed(0) : 0}%
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
